/*
 * Merch Control - custom logo cursor.
 * Ported from the approved Astro wireframe (Base.astro / tests/logo-pattern-cursor-test.html,
 * see docs/round1/wireframe-spec.md section 5.1). Dependency-free, plain rAF loop, no canvas.
 *
 * - (pointer: fine) only - never runs on touch/coarse-pointer devices.
 * - Tracks the pointer 1:1, written straight from the event and throttled to one transform per
 *   frame. The old eased lerp (ease 0.22) sat a constant v * 3.55 behind the pointer - roughly
 *   140px behind on a fast drag - which is the lag Harvie reported. No ease value fixes that:
 *   any ease < 1 is a permanent velocity-proportional offset, so the trail is gone entirely.
 *   The feature is now motion-free by construction; prefers-reduced-motion only needs to kill
 *   the width transition, which it does in mc-cursor.css.
 * - Grows 30px -> 46px over links/buttons. Stays visible everywhere, form fields included.
 *   Hover state uses delegation on document, so it also works inside drawers, modals and any
 *   other markup injected after load.
 * - Cancels every native HTML5 drag while the custom cursor is running. See the long comment
 *   on the dragstart listener below - this is the fix for the native arrow appearing on click.
 * - Never draws the mark at a guessed position. The first real mousemove seeds it, or a
 *   coordinate carried across a same-tab navigation in sessionStorage does.
 */
(function () {
  var mq = window.matchMedia('(pointer: fine)');
  var started = false;

  var GROW =
    'a, button, .button, .button-secondary, summary, label, [role="button"], input[type="submit"], input[type="button"], input[type="reset"]';
  /* The NATIVE list was removed 20/08/2026. It named the form controls that used to get a
     system cursor in mc-cursor.css, and the draw loop set the mark's opacity to 0 over them, so
     a form field showed the system arrow and no logo. The CSS carve-out is gone, so the mark
     must stay visible there too. Restore this and the opacity line in draw() together. */

  function init() {
    if (started || !mq.matches) return;

    var cur = document.getElementById('mc-cur');
    if (!cur) return;
    started = true;

    /* THE CLICK DEFECT. <a href> and <img> are native HTML5 drag sources and Chrome starts a
       drag after only 3px of movement with the button held - inside an ordinary click. The
       drag stops mousemove firing (drag/dragover are sent instead) so the mark freezes 3px
       from the press point, while the OS paints its own drag cursor at the true pointer
       position, on top of the stranded logo. Nothing in CSS can restyle the OS drag cursor
       once it exists, so the drag must never start.

       mc-cursor.css does this with -webkit-user-drag: none, but that property is Blink/WebKit
       only (Firefox does not implement it) and it does not cover dragging an already-selected
       run of text. Cancelling dragstart closes both gaps in every engine. Capture phase so
       nothing can swallow the event first.

       Safe: the theme contains no HTML5 drag source - slideshow.js uses mousedown +
       preventDefault, layered-slideshow.js uses pointerdown, drag-zoom-wrapper.js uses
       touchstart, comparison-slider.js is a range input, and no element carries a draggable
       attribute. sections/mc-quote-form.liquid is a file DROP target, which is unaffected:
       dragstart never fires for files dragged in from the OS.

       Deliberately inside init(), so it is bound only when (pointer: fine) matches and the
       custom cursor is actually running. Touch devices, and any browser where the mark is not
       drawn, keep native dragging untouched. */
    document.addEventListener(
      'dragstart',
      function (event) {
        event.preventDefault();
      },
      true
    );

    var x = 0;
    var y = 0;
    var queued = false;
    var seeded = false;

    /* The inline transform overwrites the centring transform in mc-cursor.css, so it has to
       carry translate(-50%, -50%) itself. left/top stay at 0. */
    function draw() {
      queued = false;
      cur.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) translate(-50%,-50%)';
    }

    /* Draw once at a position we actually trust, then reveal. mc-cursor.css keeps the mark
       visibility: hidden until this runs, so the user never sees the logo claiming to be
       somewhere the pointer is not. */
    function seed() {
      seeded = true;
      draw();
      cur.style.visibility = 'visible';
    }

    /* Clicking a link is the common case, and it navigates. Without this the mark is hidden on
       every new page until the mouse moves, which after a click means the pointer is sitting
       still on a page with no cursor at all. Carrying the last coordinate across the navigation
       puts the mark back under the hand immediately. Wrapped because sessionStorage throws in
       some privacy modes. Bounds-checked in case the window was resized between pages. */
    try {
      var saved = (window.sessionStorage.getItem('mc-cur-xy') || '').split(',');
      var sx = parseFloat(saved[0]);
      var sy = parseFloat(saved[1]);
      if (
        isFinite(sx) &&
        isFinite(sy) &&
        sx >= 0 &&
        sy >= 0 &&
        sx <= window.innerWidth &&
        sy <= window.innerHeight
      ) {
        x = sx;
        y = sy;
        seed();
      }
    } catch (e) {
      /* no sessionStorage: fall back to revealing on the first mousemove */
    }

    window.addEventListener(
      'mousemove',
      function (event) {
        x = event.clientX;
        y = event.clientY;
        if (!seeded) {
          /* Draw synchronously on the very first move so the mark cannot appear for one frame
             at a stale position before the queued rAF runs. */
          seed();
          return;
        }
        if (!queued) {
          queued = true;
          requestAnimationFrame(draw);
        }
      },
      { passive: true }
    );

    /* pagehide rather than beforeunload: it fires on navigation and is bfcache-friendly. One
       write per page, so there is no per-frame cost. */
    window.addEventListener(
      'pagehide',
      function () {
        if (!seeded) return;
        try {
          window.sessionStorage.setItem('mc-cur-xy', x + ',' + y);
        } catch (e) {
          /* storage unavailable or full - the mark just waits for the first mousemove */
        }
      },
      { passive: true }
    );

    document.addEventListener(
      'mouseover',
      function (event) {
        var t = event.target;
        if (!t || !t.closest) return;
        cur.style.width = t.closest(GROW) ? '46px' : '30px';
        cur.style.opacity = '1';
      },
      { passive: true }
    );
  }

  init();

  if (!started && document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  }

  /* The CSS applies cursor: none as soon as (pointer: fine) matches. If the primary pointer
     flips to fine after load (mouse plugged into a 2-in-1, device emulation toggled) and the
     JS never starts, the user gets no cursor at all. */
  if (mq.addEventListener) {
    mq.addEventListener('change', init);
  } else if (mq.addListener) {
    mq.addListener(init);
  }
})();
